import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import useAbbreviationSettings from 'api/useAbbreviationSettings';

import { IStoreSettingsFormValues } from '../StoreSettings.types';
import { buildStoreSettingsInitialValues } from '../StoreSettings.utils';

type UseAbbreviationSettingsReturn = ReturnType<typeof useAbbreviationSettings>;

interface IUseStoreSettingsInitialValuesReturn {
  initialValues: IStoreSettingsFormValues;
  settingsQuery: UseAbbreviationSettingsReturn['getAbbreviationSettings'];
}

/**
 * Derives the Store Settings form initial values from the persisted abbreviation
 * settings, falling back to defaults for any missing field, and exposes the
 * underlying query for loading/error handling.
 *
 * @example
 * const { initialValues, settingsQuery } = useStoreSettingsInitialValues();
 */
export const useStoreSettingsInitialValues =
  (): IUseStoreSettingsInitialValuesReturn => {
    const { publisherId } = useParams();
    const { getAbbreviationSettings } = useAbbreviationSettings(publisherId);

    const initialValues = useMemo(
      () => buildStoreSettingsInitialValues(getAbbreviationSettings.data),
      [getAbbreviationSettings.data]
    );

    return { initialValues, settingsQuery: getAbbreviationSettings };
  };
